/* The cart drawer's buy box — the moving parts Liquid cannot do.
 *
 * Everything that can be rendered on the server IS, in snippets/cart-drawer-buybox.liquid, because
 * Dawn re-renders #CartDrawer from the server on every add and every quantity change and would
 * wipe anything this file drew. What is left here is the two things that are genuinely client
 * state: which province the shopper picked, and whether Checkout is allowed to be pressed.
 *
 * ---- Why there is no MutationObserver here ----
 *
 * The first version of this file watched #CartDrawer with
 * `observe(host, { childList: true, subtree: true })` and re-applied on every mutation. Applying
 * WRITES to that same subtree — the province label's text, the checkout button's disabled state —
 * so each write fired the observer, which applied, which wrote, which fired the observer. An
 * unbounded loop: the theme editor froze and the machine's fans came on.
 *
 * Dawn already says when the cart has changed. It publishes `cartUpdate` through assets/pubsub.js,
 * which is exactly the signal wanted, arrives once per change, and cannot be triggered by our own
 * writes. Never watch a tree you also write to.
 */
(() => {
  /* The same key the showroom writes, so choosing Ontario in one is choosing it in the other.
     Two pickers disagreeing about where a person lives is worse than having only one. */
  const KEY = 'sr-zone';

  const money = (cents) => '$' + (cents / 100).toFixed(2);
  const read = () => { try { return localStorage.getItem(KEY); } catch (_) { return null; } };
  const write = (v) => { try { localStorage.setItem(KEY, v); } catch (_) {} };

  function paint(root) {
    const wrap = root.querySelector('[data-cdb-zone]');
    if (!wrap) return;
    const label = wrap.querySelector('[data-cdb-zone-label]');
    const opts = [...wrap.querySelectorAll('[data-zone]')];
    if (!label || !opts.length) return;
    const saved = read();
    /* Nothing picked yet → the zone the Showroom opens on (data-default), so the two agree. */
    const chosen = opts.find((b) => b.dataset.zone === saved) || opts.find((b) => b.hasAttribute('data-default')) || opts[0];
    opts.forEach((b) => {
      const on = String(b === chosen);
      /* Only write when it would actually change. Cheap here, and the habit is what keeps a
         surface that is watched by anything else from feeding itself. */
      if (b.getAttribute('aria-selected') !== on) b.setAttribute('aria-selected', on);
    });
    const price = +chosen.dataset.price || 0;
    const name = (chosen.querySelector('strong').textContent.split('·')[0] || '').trim();
    const text = name + ' · ' + (price ? money(price) : 'Free');
    if (label.textContent !== text) label.textContent = text;
  }

  /* Checkout stays disabled below the minimum order. The bar above it already says why; a button
     that looks live and then refuses is the version of this that annoys people. */
  function gate(root) {
    const state = root.querySelector('[data-cdb-state]');
    const btn = document.getElementById('CartDrawer-Checkout');
    if (!state || !btn) return;
    const total = parseFloat(state.dataset.total) || 0;
    const min = parseFloat(state.dataset.min) || 0;
    const blocked = !(total > 0 && total >= min);
    if (btn.disabled !== blocked) btn.disabled = blocked;
    btn.classList.toggle('is-blocked', blocked);
  }

  function apply() {
    document.querySelectorAll('.cdb').forEach((root) => { paint(root); gate(root); });
  }

  document.addEventListener('click', (e) => {
    const face = e.target.closest('[data-cdb-zone-face]');
    if (face) {
      const panel = face.parentElement.querySelector('[data-cdb-zone-panel]');
      const open = panel.hidden;
      panel.hidden = !open;
      face.setAttribute('aria-expanded', String(open));
      /* The panel opens IN FLOW now — it has to, because the whole middle of the drawer is one
         scrolling region and an absolute panel is clipped by it whichever way it opens. In flow it
         can be off the bottom of that region instead, which looks identical to being clipped. So
         bring it into view; `block: 'nearest'` scrolls the least amount that does the job, and only
         the scroll container moves, not the page behind the drawer. */
      if (open) panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      return;
    }
    const pick = e.target.closest('[data-cdb-zone-panel] [data-zone]');
    if (pick) {
      write(pick.dataset.zone);
      const wrap = pick.closest('[data-cdb-zone]');
      wrap.querySelector('[data-cdb-zone-panel]').hidden = true;
      wrap.querySelector('[data-cdb-zone-face]').setAttribute('aria-expanded', 'false');
      apply();
      return;
    }
    /* Anywhere else closes it. */
    document.querySelectorAll('[data-cdb-zone-panel]').forEach((p) => {
      if (!p.hidden && !p.contains(e.target)) {
        p.hidden = true;
        p.parentElement.querySelector('[data-cdb-zone-face]').setAttribute('aria-expanded', 'false');
      }
    });
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('[data-cdb-zone-panel]').forEach((p) => (p.hidden = true));
  });

  /* ---- The grab strip: on a phone the drawer is the Showroom's bottom sheet ----
     The strip is Dawn's markup (snippets/cart-drawer.liquid) so it survives the server re-render;
     the GESTURE is the sheet primitive's own, SRSheet.grab, so the cart dismisses exactly the way a
     Filters or Collections sheet does — same drag, same tap-to-close, same flick thresholds.

     Armed idempotently, and from three places, because the strip is a new node after every
     re-render: on boot, on each cartUpdate, and — for the re-render paths that publish nothing
     before the thumb lands — from a capturing pointerover, which precedes pointerdown on touch and
     mouse alike, so the strip is wired by the time the press begins. */
  function armGrab() {
    const grab = document.querySelector('cart-drawer .cdb-grab');
    if (!grab || grab.dataset.armed || !window.SRSheet || typeof window.SRSheet.grab !== 'function') return;
    const drawer = grab.closest('cart-drawer');
    const box = grab.closest('.drawer__inner');
    if (!drawer || !box) return;
    grab.dataset.armed = '1';
    const g = window.SRSheet.grab(grab, box, {
      isOpen: () => drawer.classList.contains('active'),
      /* Close first, settle second: Dawn's close drops `active`, the stylesheet's closed state is
         translateY(100%), and settle hands the inline transform back so that state is what shows —
         a slide down from wherever the thumb left it, never a snap. */
      onDismiss: () => { if (typeof drawer.close === 'function') drawer.close(); g.settle(); },
    });
  }
  document.addEventListener('pointerover', (e) => {
    if (e.target && e.target.closest && e.target.closest('.cdb-grab')) armGrab();
  }, true);

  /* Dawn replaces the drawer's contents on every cart change and says so through pubsub.js. One
     signal per change, and — unlike watching the DOM — nothing this file writes can raise it. */
  function listen() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => { apply(); armGrab(); });
    }
    apply();
    armGrab();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', listen);
  else listen();
})();
